import { DischargeFormMeta, DischargeFormValues } from 'components/dischargeForm/DischargeInformation';
import NotesQuestion from 'components/modals/QuestionList/NotesQuestion';
import SatisfactionScaleQuestion from 'components/modals/QuestionList/SatisfactionScaleQuestion';
import YesNoQuestion from 'components/modals/QuestionList/YesNoQuestion';
import { useForm } from 'context/form';
import LocationEpisode from 'models/LocationEpisode';
import { QuestionKind, QuestionType } from 'models/QuestionTemplate';

type Props = {
  step: string;
  primaryLE: LocationEpisode;
  linkedLEs: LocationEpisode[];
};

export default function LinkedDischargeQuestions({ step, primaryLE, linkedLEs }: Props) {
  const leId = step.replace('questions_', '');
  const allLEs = [primaryLE, ...linkedLEs];
  const targetLE = allLEs.find((le) => le.id === leId);

  const { values, setValue } = useForm<DischargeFormValues, DischargeFormMeta>();

  if (!targetLE) return null;

  const questions = targetLE.dischargeTemplate?.questions ?? [];
  const orderedQuestions = [...questions].sort((a: QuestionType, b: QuestionType) => a.order - b.order);
  const isPrimary = targetLE.id === primaryLE.id;

  const getAnswerValue = (questionId: string) => {
    if (isPrimary) {
      return values.questions?.[questionId];
    }
    return values.linkedQuestions?.[targetLE.id]?.[questionId];
  };

  const handleChangeAnswer = (value: string, questionId: string) => {
    if (isPrimary) {
      const newValues = { ...values.questions, [questionId]: value };
      setValue('questions', newValues);
    } else {
      const newLinkedQuestions = {
        ...values.linkedQuestions,
        [targetLE.id]: {
          ...values.linkedQuestions?.[targetLE.id],
          [questionId]: value,
        },
      };
      setValue('linkedQuestions', newLinkedQuestions);
    }
  };

  return (
    <div>
      {orderedQuestions.map((question: QuestionType, index: number) => {
        const props = {
          index: index + 1,
          config: question.config,
          text: question.text,
          value: getAnswerValue(question.id),
          setValue: (change: string) => handleChangeAnswer(change, question.id),
        };

        switch (question.kind) {
          case QuestionKind.NOTES:
            return <NotesQuestion key={question.text} {...props} />;
          case QuestionKind.SATISFACTION_SCALE:
            return <SatisfactionScaleQuestion key={question.text} {...props} />;
          default:
            return <YesNoQuestion key={question.text} {...props} />;
        }
      })}
    </div>
  );
}
